import { useState, useEffect } from 'react';
import type { HeadContentData } from './types';
import { DEFAULT_FORM_DATA } from './types';

export const useHeadContentForm = (isOpen: boolean, initialData: any) => {
  const [formData, setFormData] = useState<HeadContentData>(DEFAULT_FORM_DATA);

  useEffect(() => {
    if (isOpen) {
      console.log('🔄 HeadContentEditModal: Modal opened, setting initial data:', initialData);
      setFormData({
        title: initialData?.title || '',
        description: initialData?.description || '',
        keywords: initialData?.keywords || '',
        ogTitle: initialData?.ogTitle || '',
        ogDescription: initialData?.ogDescription || '',
        ogImage: initialData?.ogImage || '',
        ogType: initialData?.ogType || 'website',
        twitterCard: initialData?.twitterCard || 'summary_large_image',
        twitterTitle: initialData?.twitterTitle || '',
        twitterDescription: initialData?.twitterDescription || '',
        twitterImage: initialData?.twitterImage || '',
        canonicalUrl: initialData?.canonicalUrl || '',
        favicon: initialData?.favicon || '',
        appleTouchIcon: initialData?.appleTouchIcon || '',
        customMeta: initialData?.customMeta || [],
        structuredData: initialData?.structuredData || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCustomMeta = () => {
    setFormData(prev => ({
      ...prev,
      customMeta: [...prev.customMeta, { name: '', content: '' }],
    }));
  };

  const handleCustomMetaChange = (
    index: number,
    field: 'name' | 'property' | 'content',
    value: string
  ) => {
    setFormData(prev => {
      const newCustomMeta = [...prev.customMeta];
      newCustomMeta[index] = { ...newCustomMeta[index], [field]: value };
      return { ...prev, customMeta: newCustomMeta };
    });
  };

  const handleRemoveCustomMeta = (index: number) => {
    setFormData(prev => ({
      ...prev,
      customMeta: prev.customMeta.filter((_: any, i: number) => i !== index),
    }));
  };

  const handleAddStructuredData = () => {
    setFormData(prev => ({
      ...prev,
      structuredData: [
        ...prev.structuredData,
        { '@context': 'https://schema.org', '@type': 'WebPage' },
      ],
    }));
  };

  const handleStructuredDataChange = (index: number, value: string) => {
    try {
      const parsed = JSON.parse(value);
      setFormData(prev => {
        const newStructuredData = [...prev.structuredData];
        newStructuredData[index] = parsed;
        return { ...prev, structuredData: newStructuredData };
      });
    } catch {
      // Invalid JSON, don't update
    }
  };

  const handleRemoveStructuredData = (index: number) => {
    setFormData(prev => ({
      ...prev,
      structuredData: prev.structuredData.filter((_: any, i: number) => i !== index),
    }));
  };

  const getCleanedData = () => {
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      const basicFields = [
        'title',
        'description',
        'keywords',
        'canonicalUrl',
        'favicon',
        'appleTouchIcon',
        'ogTitle',
        'ogDescription',
        'ogImage',
        'ogType',
        'twitterCard',
        'twitterTitle',
        'twitterDescription',
        'twitterImage',
        'inlineStyles',
        'inlineScripts',
      ];

      if (basicFields.includes(key)) {
        acc[key] = value || '';
      } else if (value && (typeof value !== 'string' || value.trim() !== '')) {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    if (cleanedData.customMeta) {
      cleanedData.customMeta = cleanedData.customMeta.filter(
        (meta: any) => meta.content && (meta.name || meta.property)
      );
      if (cleanedData.customMeta.length === 0) {
        delete cleanedData.customMeta;
      }
    }

    if (cleanedData.structuredData) {
      cleanedData.structuredData = cleanedData.structuredData.filter(
        (data: any) => Object.keys(data).length > 0
      );
      if (cleanedData.structuredData.length === 0) {
        delete cleanedData.structuredData;
      }
    }

    return cleanedData;
  };

  return {
    formData,
    handleInputChange,
    handleAddCustomMeta,
    handleCustomMetaChange,
    handleRemoveCustomMeta,
    handleAddStructuredData,
    handleStructuredDataChange,
    handleRemoveStructuredData,
    getCleanedData,
  };
};
